import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Typography,
  Chip,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format } from 'date-fns';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'capitalize',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Expandable Row Component
function Row({ log }) {
  const [open, setOpen] = useState(false);

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 400 && statusCode < 500) return 'warning';
    if (statusCode >= 500) return 'error';
    return 'default';
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      case 'LOGIN':
        return 'primary';
      case 'FAILED_LOGIN':
        return 'error';
      case 'READ':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <>
      <StyledTableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {format(new Date(log.createdAt), 'MMM dd, yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {format(new Date(log.createdAt), 'HH:mm:ss')}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {log.userName || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {log.userEmail || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip label={log.action} color={getActionColor(log.action)} size="small" />
        </TableCell>
        <TableCell>{log.resource}</TableCell>
        <TableCell>
          <Chip label={log.method} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip label={log.statusCode} color={getStatusColor(log.statusCode)} size="small" />
        </TableCell>
        <TableCell>
          <Typography variant="body2">{log.ipAddress || 'N/A'}</Typography>
        </TableCell>
        <TableCell>
          {log.duration ? (
            <Typography variant="body2" color={log.duration > 1000 ? 'error' : 'text.secondary'}>
              {log.duration}ms
            </Typography>
          ) : (
            'N/A'
          )}
        </TableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Details
              </Typography>

              <Stack spacing={2}>
                {/* Endpoint */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Endpoint:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    {log.method} {log.endpoint}
                  </Typography>
                </Box>

                {/* Device Info */}
                <Stack direction="row" spacing={2}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Browser:
                    </Typography>
                    <Typography variant="body2">{log.deviceInfo?.browser || 'N/A'}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      OS:
                    </Typography>
                    <Typography variant="body2">{log.deviceInfo?.os || 'N/A'}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Device:
                    </Typography>
                    <Typography variant="body2">{log.deviceInfo?.device || 'N/A'}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Platform:
                    </Typography>
                    <Typography variant="body2">{log.deviceInfo?.platform || 'N/A'}</Typography>
                  </Box>
                </Stack>

                {/* User Agent */}
                {log.userAgent && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      User Agent:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'action.hover',
                        p: 1,
                        borderRadius: 1,
                        display: 'block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {log.userAgent}
                    </Typography>
                  </Box>
                )}

                {/* Error Message */}
                {log.errorMessage && (
                  <Box>
                    <Typography variant="subtitle2" color="error">
                      Error Message:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ bgcolor: 'error.lighter', p: 1, borderRadius: 1 }}
                    >
                      {log.errorMessage}
                    </Typography>
                  </Box>
                )}

                {/* Request Body */}
                {log.requestBody && Object.keys(log.requestBody).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Request Body:
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'action.hover',
                        p: 1,
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                        {JSON.stringify(log.requestBody, null, 2)}
                      </pre>
                    </Box>
                  </Box>
                )}

                {/* Metadata */}
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Additional Info:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {Object.entries(log.metadata).map(([key, value]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${value}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Main Table Component
export default function AuditLogsTable({ logs, page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange }) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 3,
        maxHeight: 'calc(100vh - 320px)',
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: 8, height: 8 },
        '&::-webkit-scrollbar-track': { bgcolor: 'action.hover' },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'divider',
          borderRadius: 4,
          '&:hover': { bgcolor: 'text.secondary' },
        },
      }}
    >
      <Table sx={{ minWidth: 750 }} aria-label="audit logs table" stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell />
            <StyledTableCell>Date & Time</StyledTableCell>
            <StyledTableCell>User</StyledTableCell>
            <StyledTableCell>Action</StyledTableCell>
            <StyledTableCell>Resource</StyledTableCell>
            <StyledTableCell>Method</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>IP Address</StyledTableCell>
            <StyledTableCell>Duration</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Box sx={{ py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No audit logs found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters or date range
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => <Row key={log._id} log={log} />)
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              colSpan={9}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
